<template>
    <v-tab-item :value="$route.path" class="pa-3">
        <v-toolbar flat height="90">
            <v-btn
                color="secondary"
                class="text-capitalize px-5"
                depressed
                v-text="translations.add[lang]"
                @click="openAttributeDialog('create', {})"
            ></v-btn>
        </v-toolbar>
        <v-row no-gutters justify="end" class="px-4 pb-3 align-end">
            <div class="mr-auto"></div>


            <v-col cols="4" class="pa-0">
                <b-search-field v-model="search"></b-search-field>
            </v-col>
        </v-row>
        <b-data-table
            :headers="headers"
            :items="attributes"
            :page.sync="page"
            @page-sync="p => (page = p)"
            :loading="loading"
            disable-sort
            class="b-outlined"
        >
            <template v-slot:item.image_url="{ item }">
                <v-img
                    :src="item.image_url"
                    width="70"
                    v-if="item.image_url"
                ></v-img>
                <div v-else>-</div>
            </template>

            <template v-slot:item.variants_prices="{ item }">
                <span v-if="item.variants_prices.length > 1"
                    >{{ Math.min(...item.variants_prices).toFixed(2) }} -
                    {{ Math.max(...item.variants_prices).toFixed(2) }}</span
                >
                <span v-else>{{ item.variants_prices[0] || "-" }}</span>
            </template>

            <template v-slot:item.actions="{ item }">
                <v-tooltip color="secondary" top>
                    <template v-slot:activator="{ on }">
                        <v-btn
                            color="yellow darken-3"
                            icon
                            v-on="on"
                            @click="openAttributeDialog('update', item)"
                        >
                            <v-icon v-text="icons.mdiPencilOutline"></v-icon>
                        </v-btn>
                    </template>

                    <span
                        class="font-weight-bold"
                        v-text="translations.update[lang]"
                    ></span>
                </v-tooltip>

                <v-tooltip color="secondary" top>
                    <template v-slot:activator="{ on }">
                        <v-btn
                            color="red"
                            icon
                            v-on="on"
                            @click="
                                () => {
                                    attribute = item;
                                    deleteDialog = true;
                                }
                            "
                        >
                            <v-icon v-text="icons.mdiClose"></v-icon>
                        </v-btn>
                    </template>

                    <span
                        class="font-weight-bold"
                        v-text="translations.delete[lang]"
                    ></span>
                </v-tooltip>
            </template>
        </b-data-table>

        <v-dialog v-model="attributeDialog" max-width="600">
            <b-card
                :title="translations[`${mode}`][this.lang]"
                :loading="loading"
                :error-message="errorMessage"
                @cancel="closeForm"
                @submit="submitForm"
                :submit-text="{ el: 'Αποθήκευση', en: 'Save', it: 'Salva' }"
                :cancel-text="{ el: 'Ακύρωση', en: 'Cancel', it: 'Cancella' }"
            >
                <b-text-field
                    v-model="attribute.name[nameLang]"
                    :label="translations.name[lang]"
                >
                    <template v-slot:append>
                        <b-lang-menu
                            v-model="nameLang"
                            type="inner"
                        ></b-lang-menu>
                    </template>
                </b-text-field>

                <b-textarea
                    v-model="attribute.description[descriptionLang]"
                    :label="translations.description[lang]"
                >
                    <template v-slot:append>
                        <b-lang-menu
                            v-model="descriptionLang"
                            type="inner"
                        ></b-lang-menu>
                    </template>
                </b-textarea>
            </b-card>
        </v-dialog>

        <v-dialog v-model="deleteDialog" max-width="500">
            <b-card
                type="delete"
                :title="translations.delete[lang]"
                :submit-text="translations.delete"
                :loading="loading"
                :error-message="errorMessage"
                @cancel="deleteDialog = false"
                @submit="deleteItem()"
            >
                <div class="subtitle-1 font-weight-medium pt-2 px-2">
                    {{ translations.deleteConfirmationMessage[lang] }}
                    <span class="font-weight-bold text--primary">{{
                        attribute.name[lang]
                    }}</span
                    >{{ translations.questionmark[lang] }}
                </div>
            </b-card>
        </v-dialog>
    </v-tab-item>
</template>

<script>
import { mdiPencilOutline, mdiClose } from "@mdi/js";

import { mapState, mapMutations, mapActions } from "vuex";
import debounce from "lodash/debounce";
import translations from "@/utils/translations/general/settings/roadcubeMarket";
import { cloneDeep } from "lodash";

export default {
    name: "Attributes",

    mixins: [translations],

    data() {
        return {
            icons: { mdiPencilOutline, mdiClose },
            page: +this.$route.query.page,
            mode: "create",
            search: "",
            attributeDialog: false,
            deleteDialog: false,
            nameLang: this.$route.params.lang,
            changeNameElAuto: true,
            changeNameItAuto: true,
            changeNameEnAuto: true,
            descriptionLang: this.$route.params.lang,
            changeDescriptionElAuto: true,
            changeDescriptionItAuto: true,
            changeDescriptionEnAuto: true,
            attributeLang: this.$route.params.lang
        };
    },

    computed: {
        ...mapState(["loading", "errorMessage"]),

        ...mapState("general/settings/roadcubeMarket/attributes", [
            "attributes",
            "attribute"
        ]),

        lang() {
            return this.$route.params.lang;
        },

        headers() {
            return [
                {
                    text: this.translations.name[this.lang],
                    value: `name['${this.lang}']`
                },
                {
                    text: this.translations.description[this.lang],
                    value: `description['${this.lang}']`
                },
                {
                    text: this.translations.actions[this.lang],
                    value: "actions"
                }
            ];
        },

        attribute: {
            get() {
                return this.$store.state.general.settings.roadcubeMarket
                    .attributes.attribute;
            },

            set(val) {
                this.setAttribute(val);
            }
        },

        query() {
            let query = "?";

            for (let key in this.$route.query) {
                query += `${key}=${this.$route.query[key]}&`;
            }

            return query.slice(0, query.length - 1);
        }
    },

    methods: {
        ...mapMutations("general/settings/roadcubeMarket/attributes", [
            "setAttribute"
        ]),
        ...mapActions("general/settings/roadcubeMarket/attributes", [
            "getAttributes",
            "create",
            "update",
            "delete"
        ]),

        openAttributeDialog(mode, item) {
            this.mode = mode;
            this.attribute = cloneDeep(item);
            this.attributeDialog = true;
        },

        handleSearch() {
            if (this.page != 1) {
                this.page = 1;
            } else {
                this.getAttributes(`?q=${this.search}`);
            }
        },

        async submitForm() {
            let item = cloneDeep(this.attribute);
            let res = await this[this.mode](item);
            if (res) {
                await this.getAttributes();
                this.closeForm();
            }
        },

        async deleteItem() {
            let item = cloneDeep(this.attribute);
            let res = await this.delete(item.attribute_id);
            if (res) {
                await this.getAttributes();
                this.closeForm();
            }
        },
        closeForm() {
            this.changeNameElAuto = true;
            this.changeNameItAuto = true;
            this.changeNameEnAuto = true;
            this.changeDescriptionElAuto = true;
            this.changeDescriptionItAuto = true;
            this.changeDescriptionEnAuto = true;
            this.search = "";
            this.attributeDialog = false;
            this.deleteDialog = false;
        }
    },

    watch: {
        $route(val) {
            if (!val.query.page) {
                this.$router.replace({
                    query: {
                        page: 1,
                        ...this.$route.query
                    }
                });
            }
            this.getAttributes(`${this.query}&q=${this.search}`);
        },

        page(page) {
            this.$router.replace({ query: { ...this.$route.query, page } });
        },

        search(val) {
            if (val == null) {
                this.getAttributes(this.query);
            } else {
                this.debouncedSearch();
            }
        },

        ["$route.params.lang"](val) {
            this.nameLang = val;
            this.descriptionLang = val;
        },

        ["attribute.name.en"](val) {
            if (this.lang == "en") {
                if (this.changeNameElAuto) {
                    this.attribute.name.el = val;
                }

                if (this.changeNameItAuto) {
                    this.attribute.name.it = val;
                }

                this.changeNameEnAuto = false;
            } else if (val != this.attribute.name[this.lang]) {
                this.changeNameEnAuto = false;
            }
        },

        ["attribute.name.el"](val) {
            if (this.lang == "el") {
                if (this.changeNameEnAuto) {
                    this.attribute.name.en = val;
                }

                if (this.changeNameItAuto) {
                    this.attribute.name.it = val;
                }

                this.changeNameElAuto = false;
            } else if (val != this.attribute.name[this.lang]) {
                this.changeNameElAuto = false;
            }
        },

        ["attribute.name.it"](val) {
            if (this.lang == "it") {
                if (this.changeNameElAuto) {
                    this.attribute.name.el = val;
                }

                if (this.changeNameEnAuto) {
                    this.attribute.name.en = val;
                }

                this.changeNameItAuto = false;
            } else if (val != this.attribute.name[this.lang]) {
                this.changeNameItAuto = false;
            }
        },

        ["attribute.description.en"](val) {
            if (this.lang == "en") {
                if (this.changeDescriptionElAuto) {
                    this.attribute.description.el = val;
                }

                if (this.changeDescriptionItAuto) {
                    this.attribute.description.it = val;
                }

                this.changeDescriptionEnAuto = false;
            } else if (val != this.attribute.description[this.lang]) {
                this.changeDescriptionEnAuto = false;
            }
        },

        ["attribute.description.el"](val) {
            if (this.lang == "el") {
                if (this.changeDescriptionEnAuto) {
                    this.attribute.description.en = val;
                }

                if (this.changeDescriptionItAuto) {
                    this.attribute.description.it = val;
                }

                this.changeDescriptionElAuto = false;
            } else if (val != this.attribute.description[this.lang]) {
                this.changeDescriptionElAuto = false;
            }
        },

        ["attribute.description.it"](val) {
            if (this.lang == "it") {
                if (this.changeDescriptionElAuto) {
                    this.attribute.description.el = val;
                }

                if (this.changeDescriptionEnAuto) {
                    this.attribute.description.en = val;
                }

                this.changeDescriptionItAuto = false;
            } else if (val != this.attribute.description[this.lang]) {
                this.changeDescriptionItAuto = false;
            }
        }
    },

    beforeCreate() {
        if (!this.$route.query.page) {
            this.$router.replace({
                query: {
                    page: 1,
                    ...this.$route.query
                }
            });
        }
    },

    created() {
        this.debouncedSearch = debounce(this.handleSearch, 500);
    },

    mounted() {
        this.getAttributes(this.query);
    }
};
</script>
<style>
.csv-input * {
    cursor: pointer;
}
</style>
